import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

// Import Breadcrumb
import Breadcrumb from "../../components/Common/Breadcrumb";
import UserAvatar from "../../components/Common/UserAvatar";

// 🔥 Import useAuthUser from your AuthProvider
import { useAuthUser } from "../../hooks/AuthProvider";
import { changePassword, hasPasswordProvider } from "../../helpers/firebaseAuth";
import { isValidPassword, PASSWORD_REQUIREMENTS } from "../../helpers/passwordValidation";

const UserProfile = () => {
  // Page title
  document.title = "Profile | Porcher";

  // ✅ Get user from AuthProvider
  const user = useAuthUser();

  // Local state for updating username (without Firebase yet)
  const [name, setName] = useState(user?.displayName || "");
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Update form state when the user data changes
  useEffect(() => {
    if (user) {
      setName(user.displayName || "Guest");
    }
  }, [user]);

  // Formik validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: name || "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please Enter Your UserName"),
    }),
    onSubmit: (values) => {
      // Instead of updating Firebase, just update local state for now
      setName(values.username);
      setSuccessMessage("Profile updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000); // Clear message after 3s
    },
  });

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!isValidPassword(newPassword)) {
      setPasswordError(PASSWORD_REQUIREMENTS);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Password updated successfully.");
    } catch (error) {
      if (error?.code === "auth/invalid-credential" || error?.code === "auth/wrong-password") {
        setPasswordError("Your current password is incorrect.");
      } else if (error?.code === "auth/too-many-requests") {
        setPasswordError("Too many attempts. Please wait and try again.");
      } else {
        setPasswordError("Unable to update your password. Please try again.");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumb title="Porcher" breadcrumbItem="Profile" />

          <Row>
            <Col lg="12">
              {successMessage && <Alert color="success">{successMessage}</Alert>}

              <Card>
                <CardBody>
                  <div className="d-flex">
                    <div className="ms-3">
                      <UserAvatar
                        photoURL={user?.photoURL}
                        name={user?.displayName}
                        email={user?.email}
                        alt="Profile avatar"
                        className="avatar-md rounded-circle img-thumbnail"
                        style={{ padding: 0 }}
                      />
                    </div>
                    <div className="flex-grow-1 align-self-center">
                      <div className="text-muted">
                        <h5>{name}</h5>
                        <p className="mb-1">{user?.email || "No Email"}</p>
                        <p className="mb-0">User ID: {user?.uid || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h4 className="card-title mb-4">Change User Name</h4>

          <Card>
            <CardBody>
              <Form
                className="form-horizontal"
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                }}
              >
                <div className="form-group">
                  <Label className="form-label">User Name</Label>
                  <Input
                    name="username"
                    className="form-control"
                    placeholder="Enter User Name"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.username || ""}
                    invalid={validation.touched.username && validation.errors.username}
                  />
                  {validation.touched.username && validation.errors.username && (
                    <FormFeedback type="invalid">{validation.errors.username}</FormFeedback>
                  )}
                </div>
                <div className="text-center mt-4">
                  <Button type="submit" color="danger">
                    Update User Name
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>

          <h4 className="card-title mb-4">Password</h4>

          <Card>
            <CardBody>
              {hasPasswordProvider(user) ? (
                <Form onSubmit={handlePasswordChange}>
                  {passwordSuccess && <Alert color="success">{passwordSuccess}</Alert>}
                  {passwordError && <Alert color="danger">{passwordError}</Alert>}
                  <Row>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label className="form-label">Current Password</Label>
                        <Input
                          type="password"
                          value={currentPassword}
                          onChange={(event) => setCurrentPassword(event.target.value)}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label className="form-label">New Password</Label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(event) => setNewPassword(event.target.value)}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label className="form-label">Confirm New Password</Label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(event) => setConfirmPassword(event.target.value)}
                          required
                        />
                      </div>
                    </Col>
                  </Row>
                  <p className="text-muted small">{PASSWORD_REQUIREMENTS}</p>
                  <Button type="submit" color="primary" disabled={passwordLoading}>
                    {passwordLoading ? "Updating..." : "Update Password"}
                  </Button>
                </Form>
              ) : (
                <Alert color="info" className="mb-0">
                  Your password is managed by your sign-in provider.
                </Alert>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
